import Box from '@redbubble/design-system/src/Box';
import Button from '@redbubble/design-system/src/Button';
import * as constants from '@redbubble/design-system/src/constants';
import Text from '@redbubble/design-system/src/Text';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import {
  MatureContentToggle,
  useMatureContentToggle,
} from '@redbubble/next/mature-content-toggle';
import React, { ReactNode } from 'react';

import styles from './MatureContentCover.module.scss';

export const messages = defineMessages({
  disclaimer: {
    defaultMessage:
      'Previews for this item are not visible because mature content has been hidden.',
  },
  shortDisclaimer: { defaultMessage: 'Mature Content' },
  displayContent: { defaultMessage: 'Display mature content' },
});

export enum MatureContentCoverStyle {
  Default,
  MediaCard,
  EyesOnly,
  SearchResultsPageProductCard,
}

export interface MatureContentCoverProps {
  matureContent?: boolean;
  aspectRatioHeight: number;
  styleType?: MatureContentCoverStyle;
  children: ReactNode;
}

export const MatureContentCover: React.FC<MatureContentCoverProps> = ({
  matureContent,
  aspectRatioHeight,
  styleType,
  children,
}) => {
  const intl = useIntl();
  const setMatureContent = useMatureContentToggle();

  const searchResultsPageProductCardStyle =
    styleType === MatureContentCoverStyle.SearchResultsPageProductCard;
  const mediaCardStyle =
    styleType === MatureContentCoverStyle.MediaCard ||
    styleType === MatureContentCoverStyle.SearchResultsPageProductCard;
  const eyesOnlyStyle = styleType === MatureContentCoverStyle.EyesOnly;

  const aspectRatioStyle =
    styleType === MatureContentCoverStyle.SearchResultsPageProductCard
      ? {
          '--component-aspect-padding': '110%',
        }
      : {
          '--component-aspect-padding': `${(1 / aspectRatioHeight) * 100}%`,
        };
  if (!matureContent) {
    return <>{children}</>;
  }

  return (
    <MatureContentToggle>
      {(showContent: boolean) => {
        if (showContent) {
          return children;
        }

        return (
          <Box
            className={[
              styles.container,
              mediaCardStyle && styles.mediaCardStyle,
              searchResultsPageProductCardStyle &&
                styles.SearchResultsPageProductCardStyle,
              eyesOnlyStyle && styles.eyesOnlyStyle,
            ]
              .filter(Boolean)
              .join(' ')}
            style={aspectRatioStyle}
            data-testid="mature-content-cover"
          >
            <Box className={styles.content}>
              <Box>
                <Box className={styles.peekaboo}>
                  <svg
                    width="100%"
                    height="100%"
                    viewBox="0 0 62 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      className={styles.peekabooPath}
                      d="M30 20C30 30.3807 23.3386 38 16 38C8.66141 38 2 30.3807 2 20C2 9.61934 8.66141 2 16 2C23.3386 2 30 9.61934 30 20Z"
                      fill="white"
                      strokeWidth="4"
                    />
                    <ellipse
                      className={styles.peekabooEllipse}
                      cx="9"
                      cy="20"
                      rx="6"
                      ry="10"
                    />
                    <circle cx="12" cy="20" r="1" fill="white" />
                    <circle cx="11.5" cy="16.5" r="1.5" fill="white" />
                    <path
                      d="M60 20C60 30.3807 53.3386 38 46 38C38.6614 38 32 30.3807 32 20C32 9.61934 38.6614 2 46 2C53.3386 2 60 9.61934 60 20Z"
                      fill="white"
                      className={styles.peekabooPath}
                      strokeWidth="4"
                    />
                    <ellipse
                      className={styles.peekabooEllipse}
                      cx="39"
                      cy="20"
                      rx="6"
                      ry="10"
                    />
                    <circle cx="42" cy="20" r="1" fill="white" />
                    <circle cx="41.5" cy="16.5" r="1.5" fill="white" />
                  </svg>
                </Box>
              </Box>

              <Box className={styles.disclaimer}>
                <Text type={mediaCardStyle ? 'display6' : 'body'}>
                  {intl.formatMessage(
                    mediaCardStyle
                      ? messages.shortDisclaimer
                      : messages.disclaimer
                  )}
                </Text>
              </Box>

              <Box className={styles.button}>
                <Button
                  intent={constants.DESTRUCTIVE}
                  onClick={() => {
                    setMatureContent(true);
                  }}
                >
                  {intl.formatMessage(messages.displayContent)}
                </Button>
              </Box>
            </Box>
          </Box>
        );
      }}
    </MatureContentToggle>
  );
};
